import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'

interface ObraData {
	descripcionObra: string
	dimensionesPrincipales: string
	fundaciones: string
	metodosConstruccion: string
	materialesConstruccion: string
	experienciaContratista: string
	vigenciaSeguro: string
}

interface TodoRiesgoConstruccionObraScreenProps {
	onNext: (data: ObraData) => void
	onBack: () => void
	data?: Partial<ObraData>
}

export function TodoRiesgoConstruccionObraScreen({
	onNext,
	onBack,
	data,
}: TodoRiesgoConstruccionObraScreenProps) {
	const [formData, setFormData] = useState<ObraData>({
		descripcionObra: data?.descripcionObra || '',
		dimensionesPrincipales: data?.dimensionesPrincipales || '',
		fundaciones: data?.fundaciones || '',
		metodosConstruccion: data?.metodosConstruccion || '',
		materialesConstruccion: data?.materialesConstruccion || '',
		experienciaContratista: data?.experienciaContratista || '',
		vigenciaSeguro: data?.vigenciaSeguro || '',
	})

	const [errors, setErrors] = useState<Partial<Record<keyof ObraData, string>>>(
		{},
	)

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof ObraData, string>> = {}

		if (!formData.descripcionObra.trim()) {
			newErrors.descripcionObra = 'La descripción de la obra es requerida'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-4">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Descripción de la Obra
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Detalles técnicos del proyecto de construcción
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Descripción de la Obra
							</label>
							<p className="text-xs text-neutral-500">
								Información técnica detallada del proyecto
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={4}
								placeholder="Describa el tipo de construcción, uso previsto, características principales..."
								value={formData.descripcionObra}
								onChange={(e) =>
									setFormData({ ...formData, descripcionObra: e.target.value })
								}
							/>
							{errors.descripcionObra && (
								<p className="text-sm text-red-500">{errors.descripcionObra}</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Dimensiones Principales (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Longitud, altura, profundidad, luz libre, número de pisos..."
								value={formData.dimensionesPrincipales}
								onChange={(e) =>
									setFormData({
										...formData,
										dimensionesPrincipales: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Fundaciones (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Tipo de fundación, nivel máximo de excavación..."
								value={formData.fundaciones}
								onChange={(e) =>
									setFormData({ ...formData, fundaciones: e.target.value })
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Métodos de Construcción (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Técnicas y procedimientos a utilizar..."
								value={formData.metodosConstruccion}
								onChange={(e) =>
									setFormData({
										...formData,
										metodosConstruccion: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Materiales de Construcción (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Principales materiales a utilizar..."
								value={formData.materialesConstruccion}
								onChange={(e) =>
									setFormData({
										...formData,
										materialesConstruccion: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Experiencia del Contratista (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Antecedentes de obras similares..."
								value={formData.experienciaContratista}
								onChange={(e) =>
									setFormData({
										...formData,
										experienciaContratista: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Vigencia del Seguro (opcional)
							</label>
							<p className="text-xs text-neutral-500">
								Fechas, períodos de construcción, pruebas y mantenimiento
							</p>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={3}
								placeholder="Fecha inicio, duración construcción (meses), período pruebas, período mantenimiento..."
								value={formData.vigenciaSeguro}
								onChange={(e) =>
									setFormData({ ...formData, vigenciaSeguro: e.target.value })
								}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
